import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { ApiClientError } from "../lib/api/client";
import { useResetPasswordMutation } from "../lib/api/auth";

const resetSchema = z
  .object({
    password: z
      .string()
      .min(12, "Password must be at least 12 characters")
      .regex(/[a-z]/u, "Include a lowercase letter")
      .regex(/[A-Z]/u, "Include an uppercase letter")
      .regex(/[0-9]/u, "Include a number"),
    confirmPassword: z.string().min(1, "Confirm your password"),
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type ResetFormValues = z.infer<typeof resetSchema>;

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const navigate = useNavigate();
  const reset = useResetPasswordMutation();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ResetFormValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  const onSubmit = handleSubmit(async (values) => {
    if (!token) {
      setError("root", { message: "Reset token is missing or invalid." });
      return;
    }

    try {
      await reset.mutateAsync({ token, password: values.password });
      void navigate("/login", { replace: true });
    } catch (error) {
      const message =
        error instanceof ApiClientError
          ? error.message
          : "Unable to reset password.";
      setError("root", { message });
    }
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-edlab-bg px-4">
      <div className="w-full max-w-md rounded-2xl border border-edlab-line bg-white p-8 shadow-sm">
        <h1 className="text-2xl font-semibold text-edlab-ink">Choose a new password</h1>

        <form className="mt-6 grid gap-4" onSubmit={(event) => void onSubmit(event)}>
          {errors.root ? (
            <p className="edlab-error">
              {errors.root.message}
            </p>
          ) : null}

          <label className="grid gap-2 text-sm">
            New password
            <input
              type="password"
              autoComplete="new-password"
              className="edlab-input"
              {...register("password")}
            />
            {errors.password ? (
              <span className="text-xs text-red-600">{errors.password.message}</span>
            ) : null}
          </label>

          <label className="grid gap-2 text-sm">
            Confirm password
            <input
              type="password"
              autoComplete="new-password"
              className="edlab-input"
              {...register("confirmPassword")}
            />
            {errors.confirmPassword ? (
              <span className="text-xs text-red-600">
                {errors.confirmPassword.message}
              </span>
            ) : null}
          </label>

          <button
            type="submit"
            disabled={isSubmitting || reset.isPending || !token}
            className="rounded-lg bg-edlab-green px-4 py-2.5 text-sm font-medium text-white hover:bg-edlab-green-dark disabled:opacity-50"
          >
            Update password
          </button>
        </form>

        <p className="mt-6 text-center text-sm">
          <Link to="/login" className="text-edlab-green hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
